<template>
  <div v-loading="loading" class="board-page">
    <p v-if="!loading && error" class="err">{{ error }}</p>
    <p v-else-if="!loading && !current" class="empty-import">
      尚未成功导入
      <router-link :to="{ name: 'data-source' }">去数据源同步</router-link>
    </p>
    <template v-else-if="!loading && current">
      <div class="board-panel">
        <div class="board-panel__filters">
          <TableFilters :items="boardFilterItems" :value="rowFilters" @input="onRowFiltersInput" />
          <el-button size="small" plain @click="clearFilters">清空</el-button>
          <span class="filters__count">命中 {{ matchedRows.length }} / {{ allRows.length }} 条</span>
        </div>

        <div class="board-panel__board">
          <div class="lanes" role="list">
        <section
          v-for="lane in laneList"
          :key="lane.status"
          class="lane"
          :class="{ 'lane--collapsed': !lane.expanded, 'lane--empty': lane.count === 0 }"
          role="listitem"
        >
          <button
            type="button"
            class="lane__head"
            :aria-expanded="lane.expanded"
            @click="toggleLane(lane.status, lane.count)"
          >
            <i
              class="lane__caret el-icon-arrow-down"
              :class="{ 'lane__caret--folded': !lane.expanded }"
              aria-hidden="true"
            />
            <span class="lane__dot" :style="{ background: lane.color }" aria-hidden="true" />
            <span class="lane__title" :title="lane.label">{{ lane.label }}</span>
            <span class="lane__count">{{ lane.count }}</span>
          </button>
          <div v-show="lane.expanded" class="lane__body">
            <p v-if="lane.count === 0" class="lane__empty">暂无</p>
            <button
              v-for="row in lane.rows"
              :key="row.sourceRow"
              type="button"
              class="card"
              :style="{ borderLeftColor: lane.color }"
              @click="openRow(row)"
            >
              <div class="card__top">
                <span class="card__part">{{ partTitle(row) }}</span>
                <el-tag v-if="daysOf(row) != null" size="mini" type="danger">超期 {{ daysOf(row) }} 天</el-tag>
                <el-tag
                  v-else-if="lane.status === 'defectPending' && row.replyPlan"
                  size="mini"
                  type="warning"
                >
                  限 {{ shortPlan(row.replyPlan) }}
                </el-tag>
              </div>
              <div class="card__meta">{{ boardMeta(row) }}</div>
              <div class="card__desc">{{ row.yc || '—' }}</div>
              <div v-if="cardFields(row).length" class="card__fields">
                <div v-for="(line, idx) in cardFields(row)" :key="idx">
                  <b>{{ line.label }}：</b>
                  <span :class="{ 'card__hl': line.highlight }">{{ line.text }}</span>
                </div>
              </div>
              <div class="card__foot">
                <span class="card__foot-left">负责人 {{ row.fzr || '—' }}</span>
                <span v-if="filled(row.replyPlan) || filled(row.replyActual)" class="card__foot-right">
                  <template v-if="filled(row.replyPlan)">要求完成 {{ shortPlan(row.replyPlan) }}</template>
                  <template v-if="filled(row.replyActual)">
                    · 实际 {{ shortPlan(row.replyActual) }}（{{ row.replyOntime || '未判定' }}）
                  </template>
                </span>
              </div>
            </button>
          </div>
        </section>
          </div>
        </div>
      </div>

      <ComplaintDetailDrawer
        :visible="drawerOpen"
        :row="activeRow"
        :person-options="fzrOptions"
        @close="drawerOpen = false"
      />
    </template>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

import { ComplaintRow, CurrentImport, fetchCurrent } from '@/api/import';
import { ComplaintDetailDrawer } from '@/components';
import { TableFilters } from '@/components/crud';
import type { FilterItem } from '@/components/crud/types';
import {
  type ClosureStatus,
  CLOSURE_STATUS_LABEL,
  closureStatusOf,
  overdueDays,
} from '@/domain/complaint-closure';
import {
  complaintBoardMeta,
  complaintCardFieldLines,
  complaintPartTitle,
  complaintShortPlan,
} from '@/domain/complaint-present';
import {
  type ComplaintRowFilterState,
  buildComplaintRowFilterItems,
  emptyComplaintRowFilters,
  filterComplaintRows,
} from '@/domain/complaint-row-filters';
import { SIM_TODAY } from '@/utils/time';

interface ZoneMeta {
  status: ClosureStatus;
  color: string;
}

const ZONES: ZoneMeta[] = [
  { status: 'overdueReply', color: '#d64545' },
  { status: 'awaitingReply', color: '#d9822b' },
  { status: 'defectPending', color: '#d9822b' },
  { status: 'temp', color: '#d9822b' },
  { status: 'verifying', color: '#3b6fe0' },
  { status: 'closed', color: '#22a06b' },
];

type LaneExpandMode = 'auto' | boolean;

function filled(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

export default Vue.extend({
  name: 'ClosureBoard',
  components: { ComplaintDetailDrawer, TableFilters },
  data() {
    const laneExpand: Record<ClosureStatus, LaneExpandMode> = {
      overdueReply: 'auto',
      awaitingReply: 'auto',
      defectPending: 'auto',
      temp: 'auto',
      verifying: 'auto',
      closed: 'auto',
    };
    return {
      loading: true,
      error: '' as string,
      current: null as CurrentImport | null,
      rowFilters: emptyComplaintRowFilters() as ComplaintRowFilterState,
      laneExpand,
      drawerOpen: false,
      activeRow: null as ComplaintRow | null,
    };
  },
  computed: {
    allRows(): ComplaintRow[] {
      return this.current?.rows || [];
    },
    boardFilterItems(): FilterItem[] {
      const keys = new Set(['q', 'deptP', 'fzr', 'closure']);
      return buildComplaintRowFilterItems(this.allRows).filter((item) => keys.has(item.key));
    },
    fzrOptions(): string[] {
      return unique(this.allRows.map((row) => row.fzr || ''));
    },
    matchedRows(): ComplaintRow[] {
      return filterComplaintRows(this.allRows, this.rowFilters);
    },
    laneList(): Array<ZoneMeta & { count: number; rows: ComplaintRow[]; expanded: boolean }> {
      return ZONES.map((zone) => {
        const rows = this.matchedRows
          .filter((row) => closureStatusOf(row, SIM_TODAY) === zone.status)
          .slice()
          .sort((a, b) => {
            const delta = (overdueDays(b, SIM_TODAY) ?? 0) - (overdueDays(a, SIM_TODAY) ?? 0);
            if (delta !== 0) return delta;
            return String(a.date || '').localeCompare(String(b.date || ''));
          });
        const count = rows.length;
        return {
          ...zone,
          label: CLOSURE_STATUS_LABEL[zone.status],
          count,
          rows,
          expanded: this.isLaneExpanded(zone.status, count),
        };
      });
    },
  },
  created() {
    this.loadCurrent();
  },
  methods: {
    loadCurrent() {
      this.loading = true;
      return fetchCurrent()
        .then((payload) => {
          this.current = payload.current;
          this.error = '';
        })
        .catch((err: Error) => {
          this.error = err.message || '请求失败';
        })
        .then(() => {
          this.loading = false;
        });
    },
    isLaneExpanded(status: ClosureStatus, count: number): boolean {
      const mode = this.laneExpand[status];
      if (mode === 'auto') return count > 0;
      return mode;
    },
    toggleLane(status: ClosureStatus, count: number) {
      const next = !this.isLaneExpanded(status, count);
      this.$set(this.laneExpand, status, next);
    },
    onRowFiltersInput(value: ComplaintRowFilterState) {
      this.rowFilters = value;
    },
    clearFilters() {
      this.rowFilters = emptyComplaintRowFilters();
    },
    openRow(row: ComplaintRow) {
      this.activeRow = row;
      this.drawerOpen = true;
    },
    partTitle(row: ComplaintRow): string {
      return complaintPartTitle(row);
    },
    boardMeta(row: ComplaintRow): string {
      return complaintBoardMeta(row);
    },
    cardFields(row: ComplaintRow) {
      return complaintCardFieldLines(row);
    },
    shortPlan(value: string | null): string {
      return complaintShortPlan(value);
    },
    daysOf(row: ComplaintRow): number | null {
      return overdueDays(row, SIM_TODAY);
    },
    filled,
  },
});

function unique(values: string[]): string[] {
  const set = new Set<string>();
  values.forEach((value) => {
    const text = String(value || '').trim();
    if (text) set.add(text);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'zh-CN'));
}
</script>

<style lang="scss" scoped>
.board-page {
  @include list-page-layout;
  color: $color-text;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.err {
  color: $color-danger;
  font-size: 14px;
}

.empty-import {
  font-size: 14px;
  color: $color-text-secondary;

  a {
    margin-left: 8px;
    color: $color-primary;
  }
}

/* 对齐投诉明细 Crud：白底外框 + 筛选条 + 内层内容区 */
.board-panel {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  padding: 16px 24px 14px;
  border: 1px solid $color-border;
  border-radius: $border-radius-base;
  background: $color-surface;
}

.board-panel__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  width: 100%;
  min-height: 32px;
}

.filters__kw {
  width: 260px;
}

.filters__sel {
  width: 160px;

  &--closure {
    width: 188px;
  }
}

.filters__count {
  margin-left: auto;
  color: $color-text-secondary;
  font-size: 12px;
}

.board-panel__board {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  border: 1px solid $color-border;
  border-radius: $border-radius-base;
  background: $color-surface;
  overflow: hidden;
}

.lanes {
  display: flex;
  flex: 1;
  gap: 8px;
  min-height: 0;
  padding: 10px 12px;
  overflow-x: auto;
  overflow-y: hidden;
}

.lane {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 280px;
  max-width: 360px;
  min-height: 0;
  border: 1px solid $color-border;
  border-radius: 8px;
  background: $color-surface-muted;

  &--collapsed {
    flex: 0 0 44px;
    min-width: 44px;
    max-width: 44px;
  }
}

.lane__head {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  width: 100%;
  margin: 0;
  padding: 10px 10px 8px;
  border: 0;
  border-bottom: 1px solid $color-hairline;
  background: $color-surface;
  border-radius: 8px 8px 0 0;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
}

.lane--collapsed .lane__head {
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding: 12px 4px;
  border-bottom: 0;
  border-radius: 8px;
}

.lane__caret {
  flex-shrink: 0;
  font-size: 12px;
  color: $color-text-secondary;
  transition: transform 0.15s ease;

  &--folded {
    transform: rotate(-90deg);
  }
}

.lane--collapsed .lane__caret {
  transform: rotate(-90deg);
}

.lane--collapsed.lane--empty .lane__caret--folded {
  transform: rotate(-90deg);
}

.lane__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.lane--collapsed .lane__dot {
  display: none;
}

.lane__title {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: $color-text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lane--collapsed .lane__title {
  flex: 1;
  max-height: none;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  letter-spacing: 0.12em;
  white-space: normal;
  line-height: 1.35;
  text-align: center;
}

.lane__subfilters {
  padding: 0 12px 8px;
}

.lane__count {
  flex-shrink: 0;
  min-width: 22px;
  padding: 0 6px;
  border-radius: 10px;
  background: $color-primary-light;
  color: $color-primary;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}

.lane--collapsed .lane__count {
  margin-top: auto;
}

.lane__body {
  flex: 1;
  min-height: 0;
  padding: 8px;
  overflow-x: hidden;
  overflow-y: auto;
}

.lane__empty {
  margin: 0;
  padding: 16px 8px;
  color: $color-text-secondary;
  font-size: 12px;
  text-align: center;
}

.card {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  padding: 10px 12px;
  border: 1px solid $color-border;
  border-left-width: 3px;
  border-radius: 6px;
  background: $color-surface;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: #c5cdd8;
    box-shadow: 0 1px 4px rgba(31, 39, 51, 0.06);
  }
}

.card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
}

.card__part {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
}

.card__meta {
  margin-top: 6px;
  color: $color-text-secondary;
  font-size: 11px;
  line-height: 1.4;
}

.card__desc {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  color: $color-text-regular;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card__fields {
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.45;
  color: $color-text-secondary;

  b {
    color: $color-text-regular;
    font-weight: 600;
  }
}

.card__hl {
  color: $color-danger;
}

.card__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid $color-hairline;
  font-size: 11px;
  color: $color-text-secondary;
}

.card__foot-left {
  flex: 1;
  min-width: 0;
}

.card__foot-right {
  flex-shrink: 0;
  text-align: right;
}
</style>
